import type { FC } from 'react';
import React, { useCallback, memo, useState, useMemo } from 'react';

import { Picture } from '@ui';
import icons from '@icons';
import Lightbox from '@components/Lightbox';
import { userNoAvatar } from '@images';
import ClientReviewStats from '@components/ClientReviewStats';
import type { CommentsItemPeopleItemT } from '@root/src/types';
import PeopleReviewsModal from '@components/Modals/PeopleReviewsModal';
import './index.scss';

const {
  star: { midYellow: starMYFilled, grey: starGreyFilled }
} = icons.Fill;

interface BestCommentProps {
  peopleItem?: CommentsItemPeopleItemT | null;
  rating;
  avatar;
  fio;
  date;
  text;
  photos;
  isClientReviewStatsDisabled?: boolean;
}

const BestComment: FC<BestCommentProps> = ({
  rating,
  avatar,
  fio,
  date,
  text,
  photos,
  peopleItem,
  isClientReviewStatsDisabled
}) => {
  const [isActiveFsLightBox, setIsActiveFsLightBox] = useState(false);
  const [activeSlideNumber, setActiveSlideNumber] = useState(0);

  const sources = useMemo(
    () => photos?.length && photos.map(item => item.big),
    [photos]
  );
  const types = useMemo(
    () =>
      photos?.length && photos.map(item => (item.isVideo ? 'video' : 'image')),
    [photos]
  );

  const [isPeopleReviewsModalOpened, setIsPeopleReviewsModalOpened] =
    useState(false);
  const onCloseModal = useCallback(() => {
    setIsPeopleReviewsModalOpened(false);
  }, []);
  const openModal = useCallback(() => {
    setIsPeopleReviewsModalOpened(true);
  }, []);

  const onImgClick = (index: number): void => {
    setIsActiveFsLightBox(prev => !prev);
    setActiveSlideNumber(index + 1);
  };
  return (
    <>
      {isPeopleReviewsModalOpened && peopleItem && (
        <PeopleReviewsModal
          peopleItem={peopleItem}
          onCloseModal={onCloseModal}
        />
      )}
      <div className='best-comment'>
        <div className='best-comment__user'>
          <div onClick={openModal} className='best-comment__avatar-wrapper'>
            <img
              src={avatar || userNoAvatar}
              alt={fio}
              className='best-comment__avatar'
            />
          </div>
          <div className='best-comment__user-info'>
            <div onClick={openModal} className='best-comment__name'>
              {fio}
            </div>
            {peopleItem && (
              <ClientReviewStats
                isModalDisabled={isClientReviewStatsDisabled}
                openModal={openModal}
                className='justify-between w-full text-xs'
                withoutDateIcon
                peopleItem={peopleItem}
              />
            )}
          </div>
        </div>
        <div className='flex items-center justify-between w-full mb-2'>
          <div className='flex items-center gap-x-px'>
            {Array.from({ length: rating || 5 }, (_, i) => (
              <div key={`best-comment-star-${i}`}>
                <Picture
                  icon={i < rating ? starMYFilled : starGreyFilled}
                  width={20}
                  height={20}
                  alt='Star'
                  className='best-comment__star'
                />
              </div>
            ))}
          </div>
          <div className='best-comment__date'>{date}</div>
        </div>
        <div className='best-comment__text'>{text}</div>
        {!!photos?.length && (
          <div className='best-comment__attachments'>
            {photos.slice(0, 4).map((item, index: number) => (
              <div
                key={item.id}
                className='best-comment__attachment'
                onClick={() => {
                  onImgClick(index);
                }}
              >
                <img
                  className='best-comment__image'
                  src={item.thumb}
                  alt={`Фото ${index + 1} к отзыву`}
                />
              </div>
            ))}
          </div>
        )}
        <Lightbox
          toggler={isActiveFsLightBox}
          sources={sources}
          slide={activeSlideNumber}
          type='image'
          types={types}
        />
      </div>
    </>
  );
};

export default memo(BestComment);
